import { apiFactory, headers } from '~/src/dash/api/api-factory';
import {
  fromAPIHeaderBidderWrapper,
  IChannelList,
  ISitesInfo,
  retryFunc,
  transformSiteWrapper,
} from '~/src/dash/api/console-api';
import {
  parseArray,
  parseNumber,
  parseString,
  transformModel,
} from '~/src/dash/api/resource-transforms';
import { createUrl, restPatch, restPost } from '~/src/dash/api/rest';
import { Auth } from '~/src/dash/api/transform-auth';

export const toAPIChannelList = transformModel<IChannelList>({
  ID: parseString(['ID', 'id']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',
  PublisherID: parseNumber(['PublisherID', 'publisher_id']),
  Name: parseString(['Name', 'name']),
  ChannelIDs: parseString(['ChannelIDs', 'channel_ids']),
  ChannelGroupIDs: parseString(['ChannelGroupIDs', 'channel_group_ids']),
});

export const transformSitesInfo = transformModel<ISitesInfo>({
  ID: parseNumber(['ID']),
  Name: parseString(['Name']),
});

export const transformChannelList = transformModel<IChannelList>({
  ID: parseNumber(['ID', 'id']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  PublisherID: parseString(['PublisherID', 'publisher_id']),
  Name: parseString(['Name', 'name']),
  ChannelIDs: parseString(['ChannelIDs', 'channel_ids']),
  ChannelGroupIDs: parseString(['ChannelGroupIDs', 'channel_group_ids']),
  Sites: parseArray(['Sites', 'sites'], transformSiteWrapper),
  SitesInfo: parseArray(['SitesInfo'], transformSitesInfo),
  HeaderBidders: parseArray(['HeaderBidders'], fromAPIHeaderBidderWrapper),
  ChannelListIsAllowed: parseNumber('ChannelListIsAllowed'),
});

export function transformChannelListWrapper(fromObj: any): IChannelList {
  return transformChannelList(fromObj);
}

export const getChannelLists = apiFactory({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'channel_lists',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    const channelLists = (json || []).map(transformChannelList);
    return {
      channelLists,
    };
  },
});

export async function updateChannelList(
  channelList: IChannelList,
): Promise<[IChannelList?, Error?]> {
  const reqUrl = createUrl('/channel_list', channelList.PublisherID.toString());
  const parsedCG = toAPIChannelList(channelList);

  try {
    const ret = (await restPatch<IChannelList>(reqUrl, parsedCG)).parsedBody;
    return [transformChannelList(ret), undefined];
  } catch (e) {
    console.error(e);
    return [undefined, e];
  }
}

export async function createChannelList(
  channelList: IChannelList,
): Promise<[IChannelList?, Error?]> {
  const reqUrl = createUrl('/channel_list', channelList.PublisherID.toString());
  const parsedCG = toAPIChannelList(channelList);
  // delete ID field since ChannelList is new
  // @ts-ignore
  delete parsedCG.ID;
  try {
    const ret = (await restPost<IChannelList>(reqUrl, parsedCG)).parsedBody;
    return [transformChannelList(ret), undefined];
  } catch (e) {
    console.error(e);
    return [undefined, e];
  }
}

// the following two objects are created to prevent misspellings and reference only viable options
// please import these objects if you need to fill in preloads, uiFields

export interface IGetChannelListParams {
  publisherID: string;
  preloads?: string[];
  uiFields?: string[];
}

// this function allows us to set defaults automatically when not passing in certain params
// ex: getchannelListsPromise({ publisherId: 7, preloads: [channelListPreloads.SITES] })
// above will create these params ?publisher_id=7&preloads=Sites&ui_fields=
export const getChannelListsPromise = ({
  publisherID,
  preloads = [], // default sends an empty array; no preloads
  uiFields = [], // default sends an empty array; returns all direct struct key-value pairs
}: IGetChannelListParams) =>
  getChannelLists({
    query: {
      publisher_id: publisherID,
      preloads: preloads.join(','),
      ui_fields: uiFields.join(','),
    },
  });
